/**
 * MarketingLayout.jsx — sticky glassmorphism top nav + brand footer for all
 * marketing pages. Wraps the outlet so every public route shares the same
 * chrome (Home, About, Services, Testimonials, Contact + Client Login).
 *
 * Design guardrails from /app/design_guidelines.json:
 *   • Cream body background · deep-navy text · gold accents.
 *   • Sharp edges (rounded-none / rounded-sm) — no soft cards, no blobs.
 *   • Cormorant Garamond for headings (`font-serif`), Outfit for body
 *     (`font-outfit`). NEVER Inter / Roboto.
 */
import { NavLink, Link, useLocation, Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowUpRight, Menu, X } from "lucide-react";

// Absolute URL to the CRM login. Marketing lives on taxsolutionindia.in;
// the CRM app + login live on gtw.co.in. When running locally we still
// need a real URL that resolves.
const PORTAL_URL = "https://gtw.co.in";

const NAV = [
    { to: "/",             label: "Home" },
    { to: "/about",        label: "About" },
    { to: "/services",     label: "Services" },
    { to: "/testimonials", label: "Testimonials" },
    { to: "/contact",      label: "Contact" },
];

export default function MarketingLayout({ children }) {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const loc = useLocation();

    useEffect(() => {
        const on = () => setScrolled(window.scrollY > 8);
        on();
        window.addEventListener("scroll", on, { passive: true });
        return () => window.removeEventListener("scroll", on);
    }, []);

    // Close mobile nav on route change
    useEffect(() => { setOpen(false); }, [loc.pathname]);

    return (
        <div className="min-h-screen bg-[#FAF3E0] text-[#0A1128] font-outfit antialiased selection:bg-[#C5A059]/40">
            {/* ─── Sticky nav ─── */}
            <header
                className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                    scrolled
                        ? "backdrop-blur-xl bg-[#FAF3E0]/85 border-b border-[#0A1128]/10"
                        : "bg-transparent"
                }`}
                data-testid="marketing-nav"
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
                        <span className="font-serif text-2xl leading-none text-[#0A1128] tracking-tight">
                            Tax Solution <span className="italic text-[#C5A059]">India</span>
                        </span>
                        <span className="hidden sm:block text-[10px] font-outfit font-semibold tracking-[0.25em] uppercase text-[#0A1128]/50">
                            Est. 2009
                        </span>
                    </Link>

                    <nav className="hidden md:flex items-center gap-8">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.to === "/"}
                                className={({ isActive }) =>
                                    `text-[13px] font-outfit font-medium tracking-wide transition-colors duration-200 ${
                                        isActive ? "text-[#0A1128]" : "text-[#0A1128]/60 hover:text-[#0A1128]"
                                    }`
                                }
                                data-testid={`nav-link-${n.label.toLowerCase()}`}
                            >
                                {n.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2">
                        <a
                            href={`${PORTAL_URL}/login`}
                            data-testid="nav-client-login"
                            className="hidden sm:inline-flex items-center gap-1.5 border border-[#0A1128] text-[#0A1128] px-4 py-1.5 text-[12px] uppercase tracking-[0.15em] font-semibold hover:bg-[#0A1128] hover:text-[#FAF3E0] transition-colors duration-300 rounded-none"
                        >
                            Client Login <ArrowUpRight size={13} />
                        </a>
                        <button
                            onClick={() => setOpen((o) => !o)}
                            className="md:hidden text-[#0A1128] p-1"
                            aria-label="menu"
                            data-testid="nav-mobile-toggle"
                        >
                            {open ? <X size={22} /> : <Menu size={22} />}
                        </button>
                    </div>
                </div>

                {/* Mobile nav */}
                {open && (
                    <div className="md:hidden border-t border-[#0A1128]/10 bg-[#FAF3E0]/95 backdrop-blur">
                        <div className="px-6 py-4 flex flex-col gap-3">
                            {NAV.map((n) => (
                                <NavLink
                                    key={n.to}
                                    to={n.to}
                                    end={n.to === "/"}
                                    className={({ isActive }) =>
                                        `text-sm font-outfit font-medium tracking-wide ${
                                            isActive ? "text-[#0A1128]" : "text-[#0A1128]/60"
                                        }`
                                    }
                                    data-testid={`nav-mobile-link-${n.label.toLowerCase()}`}
                                >
                                    {n.label}
                                </NavLink>
                            ))}
                            <a
                                href={`${PORTAL_URL}/login`}
                                className="mt-2 inline-flex items-center gap-1.5 border border-[#0A1128] text-[#0A1128] px-4 py-2 text-[12px] uppercase tracking-[0.15em] font-semibold w-max"
                            >
                                Client Login <ArrowUpRight size={13} />
                            </a>
                        </div>
                    </div>
                )}
            </header>

            {/* ─── Page outlet ─── */}
            <main className="pt-16">
                {children || <Outlet />}
            </main>

            {/* ─── Footer ─── */}
            <MarketingFooter />
        </div>
    );
}

function MarketingFooter() {
    return (
        <footer className="bg-[#0A1128] text-[#FAF3E0]/80 mt-24" data-testid="marketing-footer">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-8">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
                    <div className="col-span-2">
                        <div className="font-serif text-3xl text-[#FAF3E0] leading-tight">
                            Tax Solution <span className="italic font-light">India</span>
                        </div>
                        <p className="text-sm text-[#FAF3E0]/60 mt-3 max-w-md leading-relaxed font-light">
                            Serving government, non-government and NRI clients across
                            Assam, Arunachal Pradesh &amp; Meghalaya — and abroad —
                            with trusted tax &amp; accounting since 2009.
                        </p>
                    </div>

                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059] mb-4 font-semibold">
                            Explore
                        </div>
                        <ul className="space-y-2 text-sm">
                            {NAV.map((n) => (
                                <li key={n.to}>
                                    <Link
                                        to={n.to}
                                        className="hover:text-[#C5A059] transition-colors duration-200"
                                        data-testid={`footer-link-${n.label.toLowerCase()}`}
                                    >
                                        {n.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059] mb-4 font-semibold">
                            Contact
                        </div>
                        <ul className="space-y-2 text-sm text-[#FAF3E0]/70 leading-relaxed">
                            <li>Banipur Tiniali,<br />Dibrugarh, Assam · India</li>
                            <li>
                                <a href="tel:+919435874898" className="hover:text-[#C5A059]" data-testid="footer-phone">
                                    +91 94358 74898
                                </a>
                            </li>
                            <li className="text-[#FAF3E0]/40 italic">Email — coming soon</li>
                        </ul>
                    </div>
                </div>

                {/* Massive brand mark */}
                <div className="mt-16 pt-8 border-t border-[#FAF3E0]/10">
                    <div className="font-serif text-[15vw] md:text-[10vw] lg:text-[8vw] leading-none text-[#FAF3E0]/8 select-none tracking-tight text-center">
                        Tax Solution India
                    </div>
                </div>

                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 mt-6 text-[11px] text-[#FAF3E0]/40">
                    <div>© {new Date().getFullYear()} Ghatoshkash Tax Wizards Pvt. Ltd. — All rights reserved.</div>
                    <div className="italic">Brand: Tax Solution India · Est. 2009 · Incorporated 2025</div>
                </div>
            </div>
        </footer>
    );
}
